if (!customElements.get('infinite-product-grid')) {
  class InfiniteProductGrid extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.loading = false;
      this.page = 1;
      this.hasMore = true;
    }

    connectedCallback() {
      this.grid = this.querySelector('[data-product-grid]');
      this.sentinel = this.querySelector('[data-infinite-sentinel]');
      if (!this.grid || !this.sentinel) return;

      this.collectionUrl = this.dataset.collectionUrl;
      this.ajaxView = this.dataset.ajaxView || 'product-grid-ajax';
      if (!this.collectionUrl) return;

      this.awaitSort = this.dataset.awaitSort === 'true';

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadMore();
          }
        },
        { rootMargin: '4000px 0px' }
      );

      this.observer.observe(this.sentinel);
      this.removeExcludedFromGrid();

      if (this.awaitSort) {
        this.loadPage(1, { replace: true })
          .then(() => {
            this.removeExcludedFromGrid();
            this.markReady();
            this.loadMore();
          })
          .catch(() => {
            this.removeExcludedFromGrid();
            this.markReady();
          });
        return;
      }

      this.markReady();
      this.loadMore();
    }

    markReady() {
      this.classList.remove('infinite-product-grid--awaiting');
      this.classList.add('infinite-product-grid--ready');
      this.removeAttribute('data-await-sort');
      this.grid?.removeAttribute('aria-busy');
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    buildUrl(page) {
      const parsed = new URL(this.collectionUrl, window.location.origin);
      const url = new URL(parsed.pathname, window.location.origin);
      url.searchParams.set('view', this.ajaxView);
      url.searchParams.set('sort_by', 'created-descending');
      url.searchParams.set('page', String(page));
      url.searchParams.set('per_page', this.dataset.perPage || '24');
      url.searchParams.set('image_ratio', this.dataset.imageRatio || 'portrait');
      url.searchParams.set('image_shape', this.dataset.imageShape || 'default');
      url.searchParams.set('show_secondary_image', this.dataset.showSecondaryImage || 'false');
      url.searchParams.set('show_vendor', this.dataset.showVendor || 'false');
      url.searchParams.set('show_rating', this.dataset.showRating || 'false');
      url.searchParams.set('quick_add', this.dataset.quickAdd || 'none');

      const excludeProductId = this.dataset.excludeProductId;
      if (excludeProductId) {
        url.searchParams.set('exclude_product_id', excludeProductId);
      }

      const current = new URLSearchParams(window.location.search);
      const previewThemeId = current.get('preview_theme_id');
      if (previewThemeId) {
        url.searchParams.set('preview_theme_id', previewThemeId);
      }

      return url;
    }

    isExcludedItem(item) {
      if (!item) return false;

      const excludeId = this.dataset.excludeProductId;
      if (excludeId) {
        if (item.dataset.gridProductId === excludeId) return true;
        if (
          item.querySelector(
            `#CardLink-product-grid-ajax-${excludeId}, #QuickAdd-${excludeId}, #title-product-grid-ajax-${excludeId}, [id$="-${excludeId}"]`
          )
        ) {
          return true;
        }
      }

      // Produktseite: aktuelles Produkt anhand der URL ausschließen
      if (document.body.classList.contains('template-product')) {
        const link = item.querySelector('a[href*="/products/"]');
        if (link) {
          try {
            const itemPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
            const currentPath = window.location.pathname.replace(/\/$/, '');
            if (itemPath && itemPath === currentPath) return true;
          } catch (e) {
            // ignore
          }
        }
      }

      return false;
    }

    filterItems(items) {
      return Array.from(items || []).filter((item) => !this.isExcludedItem(item));
    }

    removeExcludedFromGrid() {
      if (!this.grid) return;
      Array.from(this.grid.children).forEach((item) => {
        if (this.isExcludedItem(item)) item.remove();
      });
    }

    loadMore() {
      if (this.loading || !this.hasMore) return;
      return this.loadPage(this.page + 1, { replace: false });
    }

    async loadPage(page, { replace }) {
      if (this.loading || !this.sentinel) return;
      if (!replace && !this.hasMore) return;

      this.loading = true;

      try {
        const response = await fetch(this.buildUrl(page).toString(), {
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('Failed to load products');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-product-grid]');
        const newSentinel = doc.querySelector('[data-infinite-sentinel]');

        if (newGrid) {
          const items = this.filterItems(newGrid.children);
          if (replace) {
            this.grid.replaceChildren(...items);
          } else {
            items.forEach((item) => {
              this.grid.appendChild(item);
            });
          }
        }

        this.removeExcludedFromGrid();

        this.page = page;
        this.hasMore = Boolean(newSentinel?.dataset?.nextUrl);

        if (!this.hasMore) {
          this.observer?.disconnect();
          this.sentinel.remove();
          this.sentinel = null;
        }
      } catch (error) {
        console.error(error);
        if (replace) this.markReady();
      } finally {
        this.loading = false;

        if (this.sentinel && this.hasMore) {
          requestAnimationFrame(() => {
            if (!this.sentinel) return;
            const rect = this.sentinel.getBoundingClientRect();
            if (rect.top < window.innerHeight + 4000) {
              this.loadMore();
            }
          });
        }
      }
    }
  }

  customElements.define('infinite-product-grid', InfiniteProductGrid);
}
